import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  ButtonBase,
  Collapse,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import FlashOnRoundedIcon from "@mui/icons-material/FlashOnRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import DynamicFeedRoundedIcon from "@mui/icons-material/DynamicFeedRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import trustBudMark from "../../assets/logo/trustbud-mark.png";
import { DESTINATIONS } from "../../navigation/destinationLabels";
import FeedbackDialog from "./FeedbackDialog";

const NAV_ITEMS = [
  { key: DESTINATIONS.today.key, label: DESTINATIONS.today.label, path: DESTINATIONS.today.path, activePaths: ["/", "/today"], icon: FlashOnRoundedIcon },
  { key: DESTINATIONS.sprint.key, label: DESTINATIONS.sprint.label, path: DESTINATIONS.sprint.path, activePaths: ["/accountability", "/sprint", "/challenge", "/trustbud-connection-data", "/trustbuds/"], icon: TrackChangesRoundedIcon },
  { key: DESTINATIONS.updates.key, label: "Activity", path: "/activity", activePaths: ["/activity", "/updates", "/moments", "/trustbud-timeline"], icon: DynamicFeedRoundedIcon },
  { key: DESTINATIONS.connections.key, label: DESTINATIONS.connections.label, path: DESTINATIONS.connections.path, activePaths: ["/network", "/connections", "/companions"], icon: PeopleAltOutlinedIcon },
  { key: DESTINATIONS.plan.key, label: "Plan", path: "/plan", activePaths: ["/plan", "/manage-vision"], icon: MapOutlinedIcon },
  { key: DESTINATIONS.intelligence.key, label: DESTINATIONS.intelligence.label, path: DESTINATIONS.intelligence.path, activePaths: ["/intelligence"], icon: AutoAwesomeRoundedIcon },
];

function SidebarNavItem({ item, active, onNavigate, collapsed = false }) {
  const Icon = item.icon;
  const content = (
    <ButtonBase
      onClick={() => onNavigate(item.path)}
      sx={{
        width: "100%",
        justifyContent: collapsed ? "center" : "flex-start",
        alignItems: "center",
        textAlign: "left",
        borderRadius: "7px",
        px: collapsed ? 0.8 : 1.15,
        py: 0.65,
        minHeight: 34,
        color: "#5b6b82",
        fontWeight: 600,
        gap: collapsed ? 0 : 1,
        ...(active
          ? {
              backgroundColor: "#10233f",
              color: "#ffffff",
            }
          : {}),
        "&:hover": {
          backgroundColor: active ? "#10233f" : "#f7f9fd",
        },
      }}
    >
      <Box
        sx={{
          width: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      {!collapsed ? (
        <Typography variant="body2" sx={{ fontWeight: 750, fontSize: "0.9rem" }}>
          {item.label}
        </Typography>
      ) : null}
    </ButtonBase>
  );
  if (!collapsed) return content;
  return (
    <Tooltip title={item.label} placement="right">
      {content}
    </Tooltip>
  );
}

export default function AppSidebar({
  username,
  onLogout,
  profileImage = "",
  isStaff = false,
  collapsed = false,
  onToggleCollapse,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [toolsOpen, setToolsOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const currentItem = NAV_ITEMS.find((item) => {
    return (item.activePaths || [item.path]).some((path) =>
      path === "/"
        ? location.pathname === "/" || location.pathname === "/today"
        : location.pathname === path || location.pathname.startsWith(path)
    );
  });
  const utilityItems = [
    {
      key: "invite",
      label: "Invite someone",
      icon: MailOutlineRoundedIcon,
      onClick: () => window.dispatchEvent(new Event("trustbud:open-invite")),
    },
    {
      key: "plus",
      label: "TrustBud Plus",
      icon: WorkspacePremiumOutlinedIcon,
      onClick: () => navigate("/trustbud-plus"),
    },
    {
      key: "help",
      label: "How TrustBud works",
      icon: HelpOutlineRoundedIcon,
      onClick: () => navigate("/how-trustbud-works"),
    },
    ...(isStaff
      ? [
          {
            key: "admin-nudges",
            label: "Nudge audit",
            icon: AdminPanelSettingsOutlinedIcon,
            onClick: () => navigate("/admin/nudges"),
          },
          {
            key: "admin-ai",
            label: "AI debug",
            icon: AdminPanelSettingsOutlinedIcon,
            onClick: () => navigate("/admin/ai"),
          },
        ]
      : []),
    {
      key: "support",
      label: "Send feedback",
      icon: ChatBubbleOutlineRoundedIcon,
      onClick: () => setFeedbackOpen(true),
    },
  ];
  return (
    <>
    <Box
      sx={{
        width: collapsed ? 68 : 232,
        minWidth: collapsed ? 68 : 232,
        borderRight: "1px solid #e6edf5",
        backgroundColor: "#fbfcfe",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        position: "sticky",
        top: 0,
        transition: "width 180ms ease, min-width 180ms ease",
        overflow: "visible",
      }}
    >
      <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
        <ButtonBase
          onClick={onToggleCollapse}
          sx={{
            position: "absolute",
            top: 18,
            right: -13,
            width: 26,
            height: 26,
            borderRadius: "50%",
            color: "#7c8ea6",
            backgroundColor: "#ffffff",
            border: "1px solid #e6edf5",
            boxShadow: "0 6px 14px rgba(15, 23, 42, 0.08)",
            zIndex: 2,
            "&:hover": { backgroundColor: "#f5f8fc" },
          }}
        >
          {collapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ChevronLeftRoundedIcon fontSize="small" />}
        </ButtonBase>
      </Tooltip>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          px: collapsed ? 1 : 1.55,
        py: 1.35,
          borderBottom: "1px solid #eef3f8",
        }}
      >
        <Box
          onClick={() => navigate("/")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            cursor: "pointer",
          }}
        >
          <Box component="img" src={trustBudMark} alt="TrustBud" sx={{ width: 25, height: 21, flexShrink: 0 }} />
          {!collapsed ? (
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 850, color: "#13233d", letterSpacing: 0, lineHeight: 1 }}>
              TrustBud
            </Typography>
          ) : null}
        </Box>
      </Box>

      {!collapsed ? (
        <Box sx={{ px: 1, pt: 1 }}>
          <Box
            sx={{
              borderRadius: "8px",
              border: toolsOpen ? "1px solid #e9eef5" : "1px solid transparent",
              backgroundColor: toolsOpen ? "#ffffff" : "transparent",
              px: 0.55,
              py: 0.45,
              mb: 0.85,
            }}
          >
            <ButtonBase
              onClick={() => setToolsOpen((prev) => !prev)}
              sx={{
                width: "100%",
                justifyContent: "space-between",
                textAlign: "left",
                borderRadius: "6px",
                px: 0.5,
                py: 0.4,
                color: "#13233d",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.05, minWidth: 0 }}>
                <Avatar src={profileImage || ""} sx={{ width: 30, height: 30, bgcolor: "#17345d" }}>
                  {(username || "T").slice(0, 1).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "#13233d", fontSize: "0.9rem", lineHeight: 1.15 }}>
                    {username || "Account"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#7a8ca4", fontSize: "0.7rem" }}>
                    Tools
                  </Typography>
                </Box>
              </Box>
              <ExpandMoreRoundedIcon
                sx={{
                  color: "#8fa0b4",
                  transform: toolsOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 160ms ease",
                }}
              />
            </ButtonBase>

            <Collapse in={toolsOpen}>
              <Stack spacing={0.35} sx={{ mt: 0.9 }}>
                {utilityItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <ButtonBase
                      key={item.key}
                      onClick={item.onClick}
                      sx={{
                        width: "100%",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        textAlign: "left",
                        gap: 1.1,
                        borderRadius: "6px",
                        px: 1.2,
                        py: 0.6,
                        minHeight: 32,
                        color: "#5b6b82",
                        "&:hover": { backgroundColor: "#ffffff" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }} />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          flex: 1,
                          minWidth: 0,
                          textAlign: "left",
                          lineHeight: 1.3,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </ButtonBase>
                  );
                })}

                <Divider sx={{ my: 0.4, borderColor: "#e8eef5" }} />

                <ButtonBase
                  onClick={() => navigate("/account-settings")}
                  sx={{
                    width: "100%",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    textAlign: "left",
                    gap: 1.1,
                    borderRadius: "6px",
                    px: 1.2,
                    py: 0.6,
                    minHeight: 32,
                    color: "#5b6b82",
                    "&:hover": { backgroundColor: "#ffffff" },
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, textAlign: "left", fontSize: "0.85rem" }}>
                    Settings
                  </Typography>
                </ButtonBase>

                <ButtonBase
                  onClick={onLogout}
                  sx={{
                    width: "100%",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    textAlign: "left",
                    gap: 1.1,
                    borderRadius: "6px",
                    px: 1.2,
                    py: 0.65,
                    minHeight: 34,
                    color: "#5b6b82",
                    "&:hover": { backgroundColor: "#fef2f2", color: "#b91c1c" },
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <LogoutRoundedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, textAlign: "left", fontSize: "0.85rem" }}>
                    Log out
                  </Typography>
                </ButtonBase>
              </Stack>
            </Collapse>
          </Box>

          <Divider sx={{ mb: 1.2, borderColor: "#e8eef5" }} />
        </Box>
      ) : (
        <Stack spacing={0.8} sx={{ px: 0.9, pt: 1, pb: 1, alignItems: "center" }}>
          <Tooltip title={username || "Account"} placement="right">
            <Avatar src={profileImage || ""} sx={{ width: 40, height: 40, bgcolor: "#17345d" }}>
              {(username || "T").slice(0, 1).toUpperCase()}
            </Avatar>
          </Tooltip>
          <Divider sx={{ width: "100%", borderColor: "#e8eef5" }} />
        </Stack>
      )}

      <Stack spacing={0.35} sx={{ px: collapsed ? 0.8 : 1, pb: 1.4 }}>
        {NAV_ITEMS.map((item) => (
          <SidebarNavItem
            key={item.key}
            item={item}
            active={currentItem?.key === item.key}
            onNavigate={navigate}
            collapsed={collapsed}
          />
        ))}
      </Stack>

      <Box sx={{ flex: 1 }} />

      <Box sx={{ px: collapsed ? 1 : 2.2, pb: 2.1, display: "flex", justifyContent: "center" }}>
        {!collapsed ? (
          <Typography variant="caption" sx={{ color: "#8a9bb0", fontWeight: 800 }}>
            Follow-through system
          </Typography>
        ) : (
          <Stack spacing={0.65} alignItems="center">
            <Tooltip title="Send feedback" placement="right">
              <ButtonBase
                onClick={() => setFeedbackOpen(true)}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  color: "#7c8ea6",
                  "&:hover": { backgroundColor: "#f5f8fc" },
                }}
              >
                <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} />
              </ButtonBase>
            </Tooltip>
            <Tooltip title="Settings" placement="right">
              <ButtonBase
                onClick={() => navigate("/account-settings")}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  color: "#7c8ea6",
                  "&:hover": { backgroundColor: "#f5f8fc" },
                }}
              >
                <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
              </ButtonBase>
            </Tooltip>
          </Stack>
        )}
      </Box>
    </Box>
    <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} surface="sidebar" />
    </>
  );
}
